import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Users, ShieldCheck, Code2, Trash2, AlertCircle, UserPlus, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";

const DIVISION_LABELS = {
  sistem_informasi: "Sistem Informasi",
  infrastruktur_ti: "Infrastruktur TI",
  pelayanan: "Pelayanan",
  tata_kelola: "Tata Kelola",
  "": "—",
};

const ROLE_STYLES = {
  kepala_tik: "bg-purple-50 text-purple-700 border border-purple-200 dark:bg-purple-900/30 dark:text-purple-400 dark:border-purple-800",
  kapokja: "bg-indigo-50 text-indigo-700 border border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-400 dark:border-indigo-800",
  ka_div: "bg-blue-50 text-blue-700 border border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:border-blue-800",
  staf: "bg-slate-100 text-slate-700 border border-slate-300 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700",
};

const ROLE_LABELS = {
  kepala_tik: "Kepala TIK",
  kapokja: "Kapokja",
  ka_div: "Ka Div",
  staf: "staf",
};

export default function UserManagementPage() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [passwordDialog, setPasswordDialog] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: "", email: "", password: "", role: "staf", division: "" });
  const [inviting, setInviting] = useState(false);
  const navigate = useNavigate();

  // Redirect non-executives
  useEffect(() => {
    if (currentUser && !["superadmin", "kepala_tik", "kapokja"].includes(currentUser.role)) {
      navigate("/", { replace: true });
    }
  }, [currentUser, navigate]);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await API.get("/users");
      setUsers(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await API.put(`/users/${userId}/role`, { role: newRole });
      toast.success(`Role updated to ${newRole}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update role");
    }
  };

  const handleDivisionChange = async (userId, newDivision) => {
    try {
      await API.put(`/users/${userId}/division`, { division: newDivision });
      toast.success("Divisi berhasil diperbarui");
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal memperbarui divisi");
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    setDeleting(true);
    try {
      await API.delete(`/users/${deleteDialog.id}`);
      toast.success(`User ${deleteDialog.name} deleted`);
      setDeleteDialog(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeleting(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setChangingPassword(true);
    try {
      await API.put(`/users/${passwordDialog.id}/password`, { password: newPassword });
      toast.success(`Password for ${passwordDialog.name} has been updated`);
      setPasswordDialog(null);
      setNewPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update password");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteForm.name.trim() || !inviteForm.email.trim() || !inviteForm.password.trim()) {
      toast.error("All fields are required");
      return;
    }
    if (inviteForm.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setInviting(true);
    try {
      await API.post("/users", inviteForm);
      toast.success(`User ${inviteForm.name} added successfully`);
      setInviteOpen(false);
      setInviteForm({ name: "", email: "", password: "", role: "staf", division: "" });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add user");
    } finally {
      setInviting(false);
    }
  };

  const managers = users.filter((u) => u.role === "ka_div");
  const developers = users.filter((u) => u.role === "staf");
  const executives = users.filter((u) => u.role === "kepala_tik" || u.role === "kapokja");

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div data-testid="user-management-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Manajemen Pengguna
          </h1>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Kelola anggota tim dan perannya</p>
        </div>
        <Button onClick={() => setInviteOpen(true)} className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white" data-testid="add-user-button">
          <UserPlus className="w-4 h-4 mr-2" /> Tambah Pengguna
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 sm:gap-6 mb-8" data-testid="user-summary-cards">
        <Card className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-none">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500 dark:text-slate-400">Total Pengguna</p>
                <p className="text-3xl font-black text-slate-900 dark:text-slate-100 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{users.length}</p>
              </div>
              <div className="w-11 h-11 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-none">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500 dark:text-slate-400">Executives</p>
                <p className="text-3xl font-black text-slate-900 dark:text-slate-100 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{executives.length}</p>
              </div>
              <div className="w-11 h-11 rounded-lg bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-none">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500 dark:text-slate-400">Ka Div</p>
                <p className="text-3xl font-black text-slate-900 dark:text-slate-100 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{managers.length}</p>
              </div>
              <div className="w-11 h-11 rounded-lg bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-none">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500 dark:text-slate-400">Staf</p>
                <p className="text-3xl font-black text-slate-900 dark:text-slate-100 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{developers.length}</p>
              </div>
              <div className="w-11 h-11 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center">
                <Code2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Users Table */}
      <Card className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-none overflow-hidden">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold text-slate-900 dark:text-white flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            Anggota Tim ({users.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table data-testid="users-table">
            <TableHeader className="bg-slate-50 dark:bg-slate-900/50">
              <TableRow className="bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                <TableHead className="p-4 font-semibold text-slate-700 dark:text-slate-300">Nama</TableHead>
                <TableHead className="p-4 font-semibold text-slate-700 dark:text-slate-300 hidden sm:table-cell">Email</TableHead>
                <TableHead className="p-4 font-semibold text-slate-700 dark:text-slate-300">Peran</TableHead>
                <TableHead className="p-4 font-semibold text-slate-700 dark:text-slate-300 hidden lg:table-cell">Divisi</TableHead>
                <TableHead className="p-4 font-semibold text-slate-700 dark:text-slate-300 hidden md:table-cell">Otentikasi</TableHead>
                <TableHead className="p-4 font-semibold text-slate-700 dark:text-slate-300 hidden lg:table-cell">Bergabung</TableHead>
                <TableHead className="p-4 w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => {
                const isSelf = u.id === currentUser?.id;
                return (
                  <TableRow key={u.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors" data-testid={`user-row-${u.id}`}>
                    <TableCell className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-sm font-bold text-slate-600 dark:text-slate-300 shrink-0 overflow-hidden">
                          {u.picture ? (
                            <img 
                              src={u.picture.startsWith('http') ? u.picture : `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}${u.picture}`} 
                              alt={u.name} 
                              className="w-full h-full object-cover" 
                            />
                          ) : (
                            u.name?.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-900 dark:text-slate-100">
                            {u.name}
                            {isSelf && <span className="text-xs text-blue-600 dark:text-blue-400 ml-1.5">(Anda)</span>}
                          </p>
                          <p className="text-xs text-slate-500 dark:text-slate-400 sm:hidden">{u.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="p-4 hidden sm:table-cell">
                      <span className="text-sm text-slate-600 dark:text-slate-400">{u.email}</span>
                    </TableCell>
                    <TableCell className="p-4">
                      {["superadmin", "kepala_tik", "kapokja"].includes(currentUser?.role) && !isSelf ? (
                        <Select
                          value={u.role || "staf"}
                          onValueChange={(val) => handleRoleChange(u.id, val)}
                        >
                          <SelectTrigger
                            className="w-[130px] h-8 text-xs bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 dark:text-slate-200 rounded-md"
                            data-testid={`role-select-${u.id}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200">
                            <SelectItem value="kepala_tik">
                              <div className="flex items-center gap-2">
                                <ShieldCheck className="w-3.5 h-3.5 text-purple-600" />
                                Kepala TIK
                              </div>
                            </SelectItem>
                            <SelectItem value="kapokja">
                              <div className="flex items-center gap-2">
                                <ShieldCheck className="w-3.5 h-3.5 text-indigo-600" />
                                Kapokja
                              </div>
                            </SelectItem>
                            <SelectItem value="ka_div">
                              <div className="flex items-center gap-2">
                                <ShieldCheck className="w-3.5 h-3.5 text-blue-600" />
                                Ka Div
                              </div>
                            </SelectItem>
                            <SelectItem value="staf">
                              <div className="flex items-center gap-2">
                                <Code2 className="w-3.5 h-3.5 text-slate-600" />
                                Staf
                              </div>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <Badge className={`${ROLE_STYLES[u.role || "staf"]} text-xs font-medium capitalize`}>
                          {["superadmin", "ka_div", "kepala_tik", "kapokja"].includes(u.role) && <ShieldCheck className="w-3 h-3 mr-1" />}
                          {u.role === "staf" && <Code2 className="w-3 h-3 mr-1" />}
                          {ROLE_LABELS[u.role || "staf"]}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="p-4 hidden lg:table-cell">
                      {["superadmin", "kepala_tik", "kapokja"].includes(currentUser?.role) && !isSelf && ["staf", "ka_div"].includes(u.role) ? (
                        <Select
                          value={u.division || "none"}
                          onValueChange={(val) => handleDivisionChange(u.id, val === "none" ? "" : val)}
                        >
                          <SelectTrigger className="w-[170px] h-8 text-xs bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 dark:text-slate-200 rounded-md">
                            <SelectValue placeholder="Pilih divisi" />
                          </SelectTrigger>
                          <SelectContent className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200">
                            <SelectItem value="none">— Langsung ke Kapokja</SelectItem>
                            <SelectItem value="sistem_informasi">Sistem Informasi</SelectItem>
                            <SelectItem value="infrastruktur_ti">Infrastruktur TI</SelectItem>
                            <SelectItem value="pelayanan">Pelayanan</SelectItem>
                            <SelectItem value="tata_kelola">Tata Kelola</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-xs text-slate-500 dark:text-slate-400">
                          {DIVISION_LABELS[u.division || ""] || "—"}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="p-4 hidden md:table-cell">
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        {u.auth_provider === "google" ? (
                          <span className="inline-flex items-center gap-1">
                            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                            Google
                          </span>
                        ) : (
                          "Email/Password"
                        )}
                      </span>
                    </TableCell>
                    <TableCell className="p-4 hidden lg:table-cell">
                      <span className="text-xs text-slate-500 dark:text-slate-400">
                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                      </span>
                    </TableCell>
                    <TableCell className="p-4">
                      <div className="flex items-center gap-1">
                        {currentUser?.role === "superadmin" && !isSelf && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-slate-400 dark:text-slate-500 hover:text-amber-600 dark:hover:text-amber-400 dark:hover:bg-slate-800"
                            onClick={() => {
                              setPasswordDialog(u);
                              setNewPassword("");
                            }}
                            title="Change Password"
                            data-testid={`change-password-${u.id}`}
                          >
                            <KeyRound className="w-4 h-4" />
                          </Button>
                        )}
                        {["superadmin", "kepala_tik", "kapokja"].includes(currentUser?.role) && !isSelf && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-slate-400 dark:text-slate-500 hover:text-red-600 dark:hover:text-red-400 dark:hover:bg-slate-800"
                            onClick={() => setDeleteDialog(u)}
                            title="Delete User"
                            data-testid={`delete-user-${u.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent className="sm:max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600 dark:text-red-500" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <AlertCircle className="w-5 h-5" />
              Hapus Pengguna
            </DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-400">
              Apakah Anda yakin ingin menghapus <strong className="text-slate-900 dark:text-slate-200">{deleteDialog?.name}</strong> ({deleteDialog?.email})?
              Tindakan ini tidak dapat dibatalkan.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)} className="border-slate-300 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800" data-testid="cancel-delete-user">
              Batal
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 text-white"
              data-testid="confirm-delete-user"
            >
              {deleting ? "Menghapus..." : "Hapus Pengguna"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={!!passwordDialog} onOpenChange={() => setPasswordDialog(null)}>
        <DialogContent className="sm:max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
          <form onSubmit={handlePasswordChange}>
            <DialogHeader>
              <DialogTitle className="text-slate-900 dark:text-slate-100 flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-amber-600 dark:text-amber-500" />
                Ubah Kata Sandi
              </DialogTitle>
              <DialogDescription className="text-slate-600 dark:text-slate-400">
                Atur kata sandi baru untuk <span className="font-semibold text-slate-900 dark:text-slate-200">{passwordDialog?.name}</span>.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password" className="text-slate-700 dark:text-slate-300">Kata Sandi Baru</Label>
                <Input
                  id="new-password"
                  type="password"
                  placeholder="Masukkan kata sandi baru (min 6 karakter)"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700 dark:text-slate-200"
                  required
                  minLength={6}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setPasswordDialog(null)} className="border-slate-300 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800">
                Batal
              </Button>
              <Button type="submit" className="bg-amber-600 hover:bg-amber-700 dark:bg-amber-500 dark:hover:bg-amber-600 text-white" disabled={changingPassword || newPassword.length < 6}>
                {changingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {changingPassword ? "Memperbarui..." : "Perbarui Kata Sandi"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add user dialog */}
      <Dialog open={inviteOpen} onOpenChange={(open) => { setInviteOpen(open); if (!open) setInviteForm({ name: "", email: "", password: "", role: "staf", division: "" }); }}>
        <DialogContent className="sm:max-w-md bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800" data-testid="add-user-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 dark:text-slate-100" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <UserPlus className="w-5 h-5 text-blue-600 dark:text-blue-500" />
              Tambah Pengguna Baru
            </DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-400">
              Buat akun baru untuk anggota tim. Mereka dapat masuk menggunakan kredensial yang Anda atur.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleInvite} className="space-y-4 mt-2" data-testid="add-user-form">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Nama Lengkap</Label>
              <Input
                placeholder="misal: Fajar Hidayat"
                value={inviteForm.name}
                onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                className="bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700 dark:text-slate-200 rounded-md"
                data-testid="invite-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Email</Label>
              <Input
                type="email"
                placeholder="misal: fajar@upatik.com"
                value={inviteForm.email}
                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                className="bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700 dark:text-slate-200 rounded-md"
                data-testid="invite-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Kata Sandi</Label>
              <Input
                type="password"
                placeholder="Min. 6 karakter"
                value={inviteForm.password}
                onChange={(e) => setInviteForm({ ...inviteForm, password: e.target.value })}
                className="bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700 dark:text-slate-200 rounded-md"
                data-testid="invite-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Peran</Label>
              <Select value={inviteForm.role} onValueChange={(val) => setInviteForm({ ...inviteForm, role: val })}>
                <SelectTrigger className="w-full bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700 dark:text-slate-200 rounded-md" data-testid="invite-role-select">
                  <SelectValue placeholder="Pilih peran" />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200">
                  <SelectItem value="kepala_tik">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400" /> Kepala TIK
                    </div>
                  </SelectItem>
                  <SelectItem value="kapokja">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" /> Kapokja
                    </div>
                  </SelectItem>
                  <SelectItem value="ka_div">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" /> Ka Div
                    </div>
                  </SelectItem>
                  <SelectItem value="staf">
                    <div className="flex items-center gap-2">
                      <Code2 className="w-3.5 h-3.5 text-slate-600 dark:text-slate-400" /> Staf
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {["staf", "ka_div"].includes(inviteForm.role) && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-slate-700 dark:text-slate-300">Divisi</Label>
                <Select value={inviteForm.division || "none"} onValueChange={(val) => setInviteForm({ ...inviteForm, division: val === "none" ? "" : val })}>
                  <SelectTrigger className="w-full bg-white dark:bg-slate-950 border-slate-300 dark:border-slate-700 dark:text-slate-200 rounded-md">
                    <SelectValue placeholder="Pilih divisi (opsional)" />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200">
                    <SelectItem value="">— Langsung ke Kapokja</SelectItem>
                    <SelectItem value="sistem_informasi">Sistem Informasi</SelectItem>
                    <SelectItem value="infrastruktur_ti">Infrastruktur TI</SelectItem>
                    <SelectItem value="pelayanan">Pelayanan</SelectItem>
                    <SelectItem value="tata_kelola">Tata Kelola</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" onClick={() => setInviteOpen(false)} className="border-slate-300 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800" data-testid="cancel-add-user">
                Batal
              </Button>
              <Button type="submit" disabled={inviting} className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white" data-testid="confirm-add-user">
                {inviting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {inviting ? "Menambahkan..." : "Tambah Pengguna"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
